import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Fab,
  Tooltip,
  Box,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { goTo } from '../../shared/utils';
import { loadAccount, updateAccountOrder } from '../../redux/financeAccountSlice';
import Filter from './filter';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

const AccountCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const AccountCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const CardBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.875rem',
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 400,
}));

const PriorityChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.1)',
  color: 'rgb(0,98,105)',
  fontWeight: 700,
  fontSize: '1.125rem',
  padding: theme.spacing(1.5, 1),
  height: 'auto',
  borderRadius: '12px',
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  backgroundColor: isActive
    ? 'rgba(76, 175, 80, 0.1)' 
    : 'rgba(158, 158, 158, 0.1)',
  color: isActive 
    ? theme.palette.success.dark 
    : theme.palette.grey[600],
  fontWeight: 600,
  borderRadius: '8px',
  fontSize: '0.813rem',
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const StyledTable = styled(Table)({
  minWidth: 700,
});

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0,98,105,0.04)',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flex: 1,
  minWidth: 0,
  flexDirection: 'column',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
}));

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  zIndex: '1000',
  boxShadow: '0 8px 24px rgba(0, 98, 105, 0.4)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 12px 32px rgba(0, 98, 105, 0.5)',
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
  },
}));

const VisuallyHidden = styled('span')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
});

const handleOrder = (updateOrder, event, order, selectedOrder) => {
  event.preventDefault();
  const isAsc = order.by === selectedOrder && order.direction === 'asc';
  var direction = isAsc ? 'desc' : 'asc';
  updateOrder({ by: selectedOrder, direction });
};

function List() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const items = useSelector(state => state.financeAccount.items);
  const shouldBeUpdated = useSelector(state => state.financeAccount.shouldBeUpdated);
  const order = useSelector(state => state.financeAccount.order);
  
  const updateOrder = (order) => dispatch(updateAccountOrder(order));

  useEffect(() => {
    if (shouldBeUpdated) {
      dispatch(loadAccount());
    }
  }, [shouldBeUpdated, dispatch]);

  const renderMobileCards = () => (
    <MobileContainer>
      {(items || []).map((item) => (
        <AccountCard 
          key={item.id}
          onClick={() => goTo(navigate, `accounts/edit/${item.id}`)}
        >
          <AccountCardContent>
            <CardHeader>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {item.name ?? ''}
                </Typography>
                <StatusChip 
                  icon={item.active ? <CheckCircleIcon /> : <CancelIcon />}
                  label={item.active ? 'Ativo' : 'Inativo'} 
                  size="small"
                  isActive={item.active}
                />
              </Box>
              <PriorityChip 
                label={item.priority}
              />
            </CardHeader>
            <CardBody>
              <InfoRow>
                <InfoLabel>Responsável</InfoLabel>
                <InfoValue>{item.user?.name ?? ''}</InfoValue>
              </InfoRow>
            </CardBody>
          </AccountCardContent>
        </AccountCard>
      ))}
    </MobileContainer>
  );

  return (
    <>
      <HeaderContainer>
        <HeaderLeft>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 700,
            color: '#1a1a1a',
            margin: 0,
          }}>
            Contas
          </Typography>
          <Filter />
        </HeaderLeft>
        <HeaderRight>
          <Tooltip title="Nova Conta" aria-label="Nova Conta">
            <StyledFab
              color="primary"
              aria-label="Add"
              onClick={() => goTo(navigate, 'accounts/new')}
              size="medium"
            >
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </HeaderRight>
      </HeaderContainer>
      
      {/* Mobile FAB */}
      <Box sx={{ display: { sm: 'none' } }}>
        <Tooltip title="Nova Conta" aria-label="Nova Conta">
          <StyledFab
            color="primary"
            aria-label="Add"
            onClick={() => goTo(navigate, 'accounts/new')}
          >
            <AddIcon />
          </StyledFab>
        </Tooltip>
      </Box>
      
      {/* Mobile Card View */}
      {renderMobileCards()}
      
      {/* Desktop Table View */}
      <StyledPaper>
        <StyledTable stickyHeader>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell
                sortDirection={order.by === 'name' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'name'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'name')
                  }
                >
                  Nome
                  {order.by === 'name' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'name' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'priority'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'priority')
                  }
                >
                  Prioridade
                  {order.by === 'priority' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'active' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'active'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'active')
                  }
                >
                  Ativo
                  {order.by === 'active' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>

              <StyledTableCell
                align="right"
                sortDirection={order.by === 'user' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'user'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'user')
                  }
                >
                  Responsável
                  {order.by === 'user' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {(items || []).map((item) => (
              <StyledTableRow
                hover
                key={item.id}
                onClick={() => goTo(navigate, `accounts/edit/${item.id}`)}
              >
                <StyledTableCell component="th" scope="row">
                  {item.name ?? ''}
                </StyledTableCell>
                <StyledTableCell align="right">{item.priority}</StyledTableCell>
                <StyledTableCell align="right">{item.active ? 'Sim' : 'Não'}</StyledTableCell>
                <StyledTableCell align="right">{item.user?.name ?? ''}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledPaper>
    </>
  );
}

export default List;
